import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ICategoria, ICategoriaCliente, ILinkMenuMobile } from 'modelos/src';

/**
 * Modelo del form unificado de cliente (crear + editar).
 *
 * La diferencia central con `crear-editar-cliente` / `crear-cliente-wizard` / `provisionador`:
 * acá la lista de categorías del cliente NO vive en un FormArray del form raíz. Vive en un
 * `CategoriaEditable[]` que es la ÚNICA fuente de verdad, y `cliente.categorias` se deriva de
 * ahí recién al guardar. Eso elimina el doble escritor de imágenes (tab General vs tab App
 * Nativa) que hoy hace que una tab pise a la otra.
 */

/** Prefijo de los ids temporales de categorías que todavía no existen en el backend. */
export const TMP_CAT = 'tmp-cat-';
/** Prefijo de los ids temporales de botones que todavía no existen en el backend. */
export const TMP_BTN = 'tmp-btn-';

export const esTmpCat = (id?: string): boolean => !!id?.startsWith(TMP_CAT);
export const esTmpBtn = (id?: string): boolean => !!id?.startsWith(TMP_BTN);

/**
 * Slots de imagen que consume la app NATIVA → campo de `ICategoriaCliente`.
 * Es el mismo mapa que usan `categoria-editor` y `provisionador` (duplicado allá dos veces);
 * acá queda uno solo.
 */
export const SLOTS_NATIVOS: Record<string, keyof ICategoriaCliente> = {
  arriba: 'imagenArriba',
  abajo: 'imagenAbajo',
  fondo: 'imagenFondo',
  sirena: 'imagenSirena',
  reflector: 'imagenReflector',
  adelante: 'imagenAdelante',
  atras: 'imagenAtras',
};

/**
 * Slots que SOLO consume la web legacy (`seguridad-boton-web`). El nativo no los mira.
 * Se dejan editables (no se pierde nada) pero detrás de un acordeón colapsado.
 */
export const SLOTS_LEGACY: {
  control: string;
  campo: keyof ICategoriaCliente;
  label: string;
}[] = [
  { control: 'sirenaPush', campo: 'imagenSirenaPush', label: 'Sirena (push)' },
  {
    control: 'reflectorPush',
    campo: 'imagenReflectorPush',
    label: 'Reflector (push)',
  },
  {
    control: 'adelantePush',
    campo: 'imagenAdelantePush',
    label: 'Adelante (push)',
  },
  { control: 'atrasPush', campo: 'imagenAtrasPush', label: 'Atrás (push)' },
];

/** Una categoría del cliente, en edición. Las tres listas se mutan in-place por drag&drop. */
export interface CategoriaEditable {
  /** `_id` real de la `ICategoria`, o `tmp-cat-N` si hay que crearla al guardar. */
  idCategoria: string;
  /** true = se crea al guardar (en blanco o duplicada de otra). */
  nueva: boolean;
  /** Datos de la entidad Categoria (nombre, variante, prioridad, color, botonera, nombreTemplate). */
  grupo: UntypedFormGroup;
  /** Botones de la categoría. Cada grupo viene de `crearGrupoBoton`; `_tmpId` = `_id` real o `tmp-btn-N`. */
  botones: UntypedFormArray;
  /** Pantalla 1 = `idsBtnsPrincipales`. */
  idsPantalla1: string[];
  /** Pantalla 2 = `idsBtnsSecundarios`. */
  idsPantalla2: string[];
  /** Pantalla 3 = `idsOtrosBotones`. */
  idsPantalla3: string[];
  /** Overrides de imagen que consume el nativo (claves de `SLOTS_NATIVOS`). */
  imagenes: UntypedFormGroup;
  /** Overrides que solo consume la web legacy (claves de `SLOTS_LEGACY`). */
  imagenesLegacy: UntypedFormGroup;
  /** Huella al construirla: si cambió, hay que hacer PUT de la Categoria. */
  snapshot: string;
  /** Nombres de los OTROS clientes que referencian esta misma categoría (badge de alcance). */
  compartidaCon: string[];
}

/** Huella de lo que se persiste en la entidad `Categoria` (las imágenes van al cliente, no acá). */
export function huellaCategoria(ed: CategoriaEditable): string {
  return JSON.stringify({
    grupo: ed.grupo.value,
    botones: ed.botones.value,
    p1: ed.idsPantalla1,
    p2: ed.idsPantalla2,
    p3: ed.idsPantalla3,
  });
}

/** ¿Hay que mandar un PUT de esta categoría? */
export function categoriaCambio(ed: CategoriaEditable): boolean {
  return ed.nueva || huellaCategoria(ed) !== ed.snapshot;
}

/** ¿Las imágenes override de esta categoría cambiaron? */
export function imagenesCambiaron(ed: CategoriaEditable): boolean {
  return !ed.imagenes.pristine || !ed.imagenesLegacy.pristine;
}

/** Etiqueta legible de una categoría (para chips, selects y carteles). */
export function etiquetaCategoria(cat?: ICategoria | null): string {
  if (!cat) return '—';
  return cat.variante ? `${cat.nombre} (${cat.variante})` : `${cat.nombre}`;
}

/** Etiqueta legible desde el form group de una `CategoriaEditable`. */
export function etiquetaEditable(ed: CategoriaEditable): string {
  const nombre = ed.grupo.get('nombre')?.value || 'Sin nombre';
  const variante = ed.grupo.get('variante')?.value;
  return variante ? `${nombre} (${variante})` : `${nombre}`;
}

/** Link con esquema (http/https/tel/mailto) o dominio sin esquema (la app antepone https://). */
export const LINK_MENU_PATTERN = /^(https?:\/\/\S+|tel:\S+|mailto:\S+|[^\s:/]+\.\S+)$/;

/** Fila de `configuracion.linksMenuMobile`. La usan el form (al cargar) y la tab (al agregar). */
export function crearLinkMenuGroup(l?: Partial<ILinkMenuMobile>): UntypedFormGroup {
  return new UntypedFormGroup({
    nombre: new UntypedFormControl(l?.nombre ?? null, Validators.required),
    link: new UntypedFormControl(l?.link ?? null, [
      Validators.required,
      Validators.pattern(LINK_MENU_PATTERN),
    ]),
    icono: new UntypedFormControl(l?.icono ?? null),
    imagen: new UntypedFormControl(l?.imagen ?? null),
  });
}
